import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import techSheetService from '../services/techSheetService';
import PalletDiagram from '../components/PalletDiagram';

const API = process.env.REACT_APP_API_URL || '';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const LB_TO_KG = 0.453592;
const KG_TO_LB = 2.20462;
const toKgWeight = (value, unit) => value == null ? null : (unit === 'kg' ? Number(value) : Number(value) * LB_TO_KG);
const fromKgWeight = (kg, unit) => unit === 'kg' ? kg : kg * KG_TO_LB;

const ALLERGEN_LABELS = {
  gluten:        'Cereals containing gluten (wheat, barley, rye, oat, spelt, kamut or their hybrid strains) and derived products (bread, dough, fresh or dry sweets, cookies, snacks)',
  crustaceans:   'Crustaceans and derived products',
  eggs:          'Eggs and derived products',
  fish:          'Fish and derived products',
  peanuts:       'Peanut and derived products',
  soy:           'Soy and derived products',
  nuts:          'Nuts, i.e. almonds, hazelnuts, walnuts, cashews, pecan nuts, Brazil nuts, pistachios, Queensland nuts and derived products, pine nuts and derived products',
  milk:          'Milk and derived products',
  mustard:       'Mustard and derived products',
  sesame:        'Sesame seed and derived products',
  sulphurDioxide:'Sulphur Dioxide and sulfites at concentration in excess of 10mg/kg or mg/l expressed as SO₂',
  lupin:         'Lupin bean and lupin products',
  mollusks:      'Mollusks and mollusk products',
};

// Net = pure product content across the pallet; Gross = case-packed weight (already
// stored on pallet.totalWeight) + the company's empty-pallet weight setting.
function computeNetGrossWeight(pallet, block, product, companyPalletWeightKg, productPresentations) {
  const weightUnit = pallet.weightUnit || 'lbs';
  // Units per case = packs per case (entered on the tech sheet block) × units per pack
  // (from the linked ProductPresentation's packaging details) — e.g. 10 double-packs
  // per case = 20 base units, not 10.
  const linkedPresentation = (productPresentations || []).find(p => String(p.id) === String(block.presentationId));
  const unitsPerPack = Number(linkedPresentation?.unitsPerRetailBox) || 1;
  const unitsPerCase = (Number(block.unitsPerMasterCase) || 0) * unitsPerPack;
  const totalCases = (Number(pallet.unitsPerLayer) || 0) * (Number(pallet.numberOfLayers) || 0);
  const unitNetKg = product?.unitNetWeightValue != null ? toKgWeight(product.unitNetWeightValue, product.unitNetWeightUnit) : null;
  const totalUnits = totalCases * unitsPerCase;
  const totalNetKg = (unitNetKg != null && totalUnits > 0) ? unitNetKg * totalUnits : null;
  const caseTotalKg = pallet.totalWeight ? toKgWeight(Number(pallet.totalWeight), weightUnit) : null;
  const totalGrossKg = caseTotalKg != null ? caseTotalKg + (companyPalletWeightKg || 0) : null;
  return {
    weightUnit,
    totalNetWeight: totalNetKg != null ? fromKgWeight(totalNetKg, weightUnit) : null,
    totalGrossWeight: totalGrossKg != null ? fromKgWeight(totalGrossKg, weightUnit) : null,
    palletWeightDisplay: companyPalletWeightKg ? fromKgWeight(companyPalletWeightKg, weightUnit) : 0,
  };
}

// unitsPerLayer is the COMBINED total (main grid + mixedZone, if any) — see
// docs/TECH_SHEET_MODULE.md and TechSheetPanel.js's computePalletDerived, which this mirrors.
function computeBlockPalletDerived(pallet) {
  const cols = Number(pallet.gridCols) || 1;
  const totalUnits = Number(pallet.unitsPerLayer) || 0;
  const mixedZone = pallet.mixedZone && Number(pallet.mixedZone.units) > 0 ? pallet.mixedZone : null;
  const mainUnits = mixedZone ? Math.max(totalUnits - mixedZone.units, 0) : totalUnits;
  const layers = Number(pallet.numberOfLayers) || 0;
  const rows = mainUnits > 0 ? Math.ceil(mainUnits / cols) : 0;
  const uL = Number(pallet.unitDimL) || 0;
  const uW = Number(pallet.unitDimW) || 0;
  const uH = Number(pallet.unitDimH) || 0;
  const pH = Number(pallet.palletH) || 0;
  const mainLoadL = cols * uL;
  const mainLoadW = rows * uW;
  const mixedLoadL = mixedZone ? mixedZone.cols * uW : 0;
  const mixedLoadW = mixedZone ? mixedZone.rows * uL : 0;
  return {
    cols, rows,
    totalPackages: totalUnits * layers,
    loadH: (layers * uH).toFixed(1),
    totalH: (layers * uH + pH).toFixed(1),
    loadL: mainLoadL + mixedLoadL,
    loadW: Math.max(mainLoadW, mixedLoadW),
    mixedZone,
  };
}

export default function TechSheetPreviewPage() {
  const { id } = useParams();
  const [sheet, setSheet] = useState(null);
  const [recipe, setRecipe] = useState(null);
  const [nutrition, setNutrition] = useState(null);
  const [company, setCompany] = useState(null);
  const [computedData, setComputedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const s = await techSheetService.get(id);
        setSheet(s);

        const [recipeRes, nutritionRes, companyRes, computedRes] = await Promise.allSettled([
          fetch(`${API}/api/products/${s.productId}/recipe`, auth()).then(r => r.json()),
          fetch(`${API}/api/nutrition/products/${s.productId}`, auth()).then(r => r.json()),
          fetch(`${API}/api/companies`, auth()).then(r => r.json()),
          techSheetService.getComputedData(s.productId),
        ]);

        if (recipeRes.status === 'fulfilled') setRecipe(recipeRes.value);
        if (nutritionRes.status === 'fulfilled') setNutrition(nutritionRes.value);
        if (companyRes.status === 'fulfilled') {
          const companies = companyRes.value;
          setCompany(Array.isArray(companies) ? companies[0] : companies);
        }
        if (computedRes.status === 'fulfilled') setComputedData(computedRes.value);
      } catch (e) {
        setError('Could not load tech sheet data.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  if (loading) return <div style={{ textAlign: 'center', padding: 60, fontFamily: 'sans-serif', color: '#666' }}>Loading tech sheet…</div>;
  if (error) return <div style={{ textAlign: 'center', padding: 60, color: '#c00' }}>{error}</div>;
  if (!sheet) return null;

  const product = sheet.Product || {};
  const allergens = computedData?.allergens || {};

  // Editable, pre-filled ingredient statement takes precedence; falls back to the
  // shared live computation (weight-sorted, sub-ingredients expanded), then finally to
  // the older recipe-endpoint join for sheets saved before either of those existed.
  const ingredientList = sheet.ingredientListOverride || computedData?.ingredientList || (recipe?.allIngredients?.length
    ? recipe.allIngredients.map(i => i.Ingredient?.labelName || i.Ingredient?.name).filter(Boolean).join(', ')
    : null);

  // GET /api/nutrition/products/:id returns the profile directly (flat object)
  const nutData = nutrition || null;

  // Each presentation (retail single, retail double, food service, ...) carries its
  // own packaging + pallet block; parse and derive per block.
  const presentationBlocks = (sheet.PresentationBlocks || []).map(b => {
    const raw = b.palletConfig;
    const pallet = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : null;
    const derived = pallet ? computeBlockPalletDerived(pallet) : null;
    const label = b.label || b.Presentation?.name || 'Presentation';
    return { ...b, pallet, derived, label };
  });
  const blocksWithPallet = presentationBlocks.filter(b =>
    b.pallet && (Number(b.pallet.unitsPerLayer) > 0 || Number(b.pallet.numberOfLayers) > 0)
  );
  const blocksWithPhoto = presentationBlocks.filter(b => b.retailPackagingImageUrl);

  const companyName = company?.name || 'PIZZACINI Corp.';
  const companyLogoUrl = company?.logoUrl || null;

  // Dynamic page numbering — only count pages that will render
  let pageNum = 0;
  const nextPage = () => { pageNum++; return pageNum; };
  const totalPages = 2 + (nutData ? 1 : 0) + blocksWithPhoto.length + blocksWithPallet.length;

  // Slug-safe product name for PDF filename
  const pdfName = [
    sheet.sheetNumber || 'ts',
    (product.name || 'product').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
    `v${sheet.version}`,
  ].join('-');

  const handlePrint = () => {
    const prev = document.title;
    document.title = pdfName;
    window.print();
    setTimeout(() => { document.title = prev; }, 1000);
  };

  return (
    <>
      <style>{`
        @page {
          margin: 0;
        }
        @media print {
          .ts-no-print { display: none !important; }
          .ts-page-break { page-break-before: always; }
          html, body { margin: 0; background: #fff !important; }
          .ts-sheet { padding: 18mm 20mm !important; max-width: none !important; width: 100% !important; }
        }
        @media screen {
          body { background: #e8e8e8; }
        }
        .ts-sheet {
          background: white;
          max-width: 210mm;
          margin: 0 auto;
          padding: 18mm 20mm;
          font-family: 'Arial', 'Helvetica', sans-serif;
          font-size: 10pt;
          color: #1a1a1a;
          box-sizing: border-box;
        }
        .ts-title {
          text-align: center;
          font-size: 18pt;
          font-weight: bold;
          letter-spacing: 0.04em;
          margin: 8pt 0 14pt;
        }
        .ts-product-block {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16pt;
          margin-bottom: 14pt;
        }
        .ts-product-img {
          width: 100%;
          max-height: 160pt;
          object-fit: contain;
          border-radius: 4pt;
        }
        .ts-spec-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 8.5pt;
        }
        .ts-spec-table td {
          padding: 4pt 6pt;
          border: 0.5pt solid #ccc;
          vertical-align: top;
        }
        .ts-spec-table td:first-child {
          font-weight: bold;
          background: #f4f4f4;
          white-space: nowrap;
          width: 38%;
        }
        .ts-info-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 8.5pt;
          margin-bottom: 10pt;
        }
        .ts-info-table td {
          padding: 5pt 8pt;
          border: 0.5pt solid #ccc;
          vertical-align: top;
        }
        .ts-info-table td.label {
          font-weight: bold;
          background: #f4f4f4;
          width: 30%;
        }
        .ts-allergen-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 8pt;
          margin-bottom: 10pt;
        }
        .ts-allergen-table th, .ts-allergen-table td {
          padding: 4pt 6pt;
          border: 0.5pt solid #ccc;
          text-align: left;
        }
        .ts-allergen-table th {
          background: #f4f4f4;
          font-weight: bold;
          text-align: center;
        }
        .ts-allergen-table td.center { text-align: center; }
        .ts-section-title {
          text-align: center;
          font-size: 16pt;
          font-weight: bold;
          letter-spacing: 0.06em;
          margin: 10pt 0 16pt;
        }
        .ts-nf-box {
          border: 1.5pt solid #1a1a1a;
          width: 260pt;
          margin: 0 auto;
          padding: 6pt 10pt;
          font-family: 'Helvetica Neue', Arial, sans-serif;
        }
        .ts-nf-title {
          font-size: 22pt;
          font-weight: 900;
          letter-spacing: -0.01em;
          line-height: 1.1;
          white-space: nowrap;
          border-bottom: 1pt solid #1a1a1a;
          padding-bottom: 2pt;
        }
        .ts-nf-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          border-bottom: 0.5pt solid #ccc;
          padding: 2pt 0;
          font-size: 8.5pt;
        }
        .ts-nf-row.bold { font-weight: bold; font-size: 10pt; border-bottom: 1pt solid #1a1a1a; }
        .ts-nf-row.thick { border-bottom: 4pt solid #1a1a1a; }
        .ts-nf-row.indent { padding-left: 10pt; }
        .ts-nf-dv { font-size: 7.5pt; color: #555; margin-top: 5pt; }
        .ts-nf100-box {
          border: 1pt solid #999;
          width: 170pt;
          padding: 6pt 8pt;
          font-family: 'Helvetica Neue', Arial, sans-serif;
        }
        .ts-nf100-title {
          font-size: 9pt;
          font-weight: 700;
          border-bottom: 1pt solid #999;
          padding-bottom: 3pt;
          margin-bottom: 3pt;
        }
        .ts-nf100-subtitle { font-size: 6.5pt; font-weight: 400; color: #777; }
        .ts-nf100-row {
          display: flex;
          justify-content: space-between;
          font-size: 7.5pt;
          padding: 1.5pt 0;
          border-bottom: 0.5pt solid #eee;
        }
        .ts-nf100-row.indent { padding-left: 8pt; color: #444; }
        .ts-pallet-block {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20pt;
          align-items: start;
          margin-bottom: 14pt;
        }
        .ts-pallet-specs {
          font-size: 8.5pt;
        }
        .ts-pallet-specs table {
          width: 100%;
          border-collapse: collapse;
        }
        .ts-pallet-specs td {
          padding: 3pt 6pt;
          border: 0.5pt solid #ccc;
        }
        .ts-pallet-specs td:first-child { font-weight: bold; background: #f4f4f4; }
        .ts-confidential {
          font-size: 6.5pt;
          color: #888;
          text-align: center;
          margin-top: 14pt;
          border-top: 0.5pt solid #ddd;
          padding-top: 4pt;
        }
        @media print {
          .ts-confidential {
            position: fixed;
            bottom: 8mm;
            left: 20mm;
            right: 20mm;
            margin-top: 0;
            background: #fff;
          }
        }
      `}</style>

      {/* Toolbar */}
      <div className="ts-no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', background: '#1b2638' }}>
        <button
          onClick={() => window.history.length > 1 ? window.history.back() : window.close()}
          style={{ background: 'rgba(255,255,255,0.12)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
        >
          ← Back
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {sheet.status === 'draft' && (
            <span style={{ color: '#fbbf24', fontSize: 13, fontWeight: 600 }}>DRAFT — not yet approved</span>
          )}
          <button
            onClick={handlePrint}
            style={{ background: 'white', color: '#1b2638', border: 'none', borderRadius: 6, padding: '8px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            Print / Export PDF
          </button>
        </div>
      </div>

      <div className="ts-sheet">

        {/* ════ PAGE 1 ════ */}
        <SheetHeader sheetNumber={sheet.sheetNumber} companyName={companyName} logoUrl={companyLogoUrl} page={nextPage()} totalPages={totalPages} version={sheet.version} date={sheet.approvedAt || sheet.createdAt} />

        <div className="ts-title">{product.name}</div>

        <div className="ts-product-block">
          {/* Product image */}
          <div>
            {sheet.productImageUrl
              ? <img src={sheet.productImageUrl} alt={product.name} className="ts-product-img" />
              : <div style={{ height: 150, background: '#f5f5f5', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 12 }}>No image</div>
            }
          </div>
          {/* Spec table */}
          <div>
            <table className="ts-spec-table">
              <tbody>
                <SpecRow label="BRAND" value={companyName} />
                <SpecRow label="SKU" value={product.sku || '—'} />
                {sheet.shelfLife && <SpecRow label="SHELF LIFE" value={sheet.shelfLife} />}
                {sheet.netWeight && <SpecRow label="NET WEIGHT" value={sheet.netWeight} />}
                {sheet.storageTemp && <SpecRow label="STORAGE TEMP." value={sheet.storageTemp} />}
                {sheet.diameter && <SpecRow label="DIAMETER" value={sheet.diameter} />}
                {sheet.countryOfOrigin && <SpecRow label="COUNTRY OF ORIGIN" value={sheet.countryOfOrigin} />}
                {sheet.countryOfProcedence && <SpecRow label="COUNTRY OF PROCEDENCE" value={sheet.countryOfProcedence} />}
              </tbody>
            </table>
          </div>
        </div>

        {/* Ingredient / info table */}
        <table className="ts-info-table">
          <tbody>
            {ingredientList && (
              <tr>
                <td className="label">INGREDIENTS</td>
                <td>{ingredientList}</td>
              </tr>
            )}
            {sheet.description && (
              <tr>
                <td className="label">DESCRIPTION</td>
                <td>{sheet.description}</td>
              </tr>
            )}
            {sheet.organolepticCharacteristics && (
              <tr>
                <td className="label">ORGANOLEPTIC<br/>CHARACTERISTICS</td>
                <td>{sheet.organolepticCharacteristics}</td>
              </tr>
            )}
            {sheet.servingInstructions && (
              <tr>
                <td className="label">SERVING INSTRUCTION</td>
                <td style={{ whiteSpace: 'pre-line' }}>{sheet.servingInstructions}</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* ════ PAGE 2 — Allergens + Packaging ════ */}
        <div className="ts-page-break" />
        <SheetHeader sheetNumber={sheet.sheetNumber} companyName={companyName} logoUrl={companyLogoUrl} page={nextPage()} totalPages={totalPages} version={sheet.version} date={sheet.approvedAt || sheet.createdAt} />

        <table className="ts-allergen-table">
          <thead>
            <tr>
              <th colSpan="3" style={{ textAlign: 'center', fontSize: '10pt', letterSpacing: '0.06em' }}>ALLERGENS</th>
            </tr>
            <tr>
              <th style={{ width: '70%' }}></th>
              <th>YES</th>
              <th>NO</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Certified Non-GMO</strong></td>
              <td className="center">{sheet.isCertifiedNonGMO ? 'X' : ''}</td>
              <td className="center">{!sheet.isCertifiedNonGMO ? 'X' : ''}</td>
            </tr>
            <tr>
              <td><strong>Certified Organic</strong></td>
              <td className="center">{sheet.isCertifiedOrganic ? 'X' : ''}</td>
              <td className="center">{!sheet.isCertifiedOrganic ? 'X' : ''}</td>
            </tr>
            {Object.entries(ALLERGEN_LABELS).map(([key, label]) => (
              <tr key={key}>
                <td>{label}</td>
                <td className="center">{allergens[key] ? 'X' : ''}</td>
                <td className="center">{!allergens[key] ? 'X' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <table className="ts-info-table">
          <tbody>
            {(sheet.lotNumberFormat || sheet.lotNumberKey) && (
              <tr>
                <td className="label">LOT NUMBER</td>
                <td>
                  {sheet.lotNumberFormat && <strong>{sheet.lotNumberFormat}</strong>}
                  {sheet.lotNumberKey && <><br/><span style={{ fontSize: '8pt' }}>{sheet.lotNumberKey}</span></>}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {presentationBlocks.length > 0 && (
          <table className="ts-info-table">
            <thead>
              <tr>
                <th style={{ padding: '4pt 6pt', border: '0.5pt solid #ccc', background: '#f4f4f4', fontSize: '8pt', textAlign: 'left' }}>PRESENTATION</th>
                <th style={{ padding: '4pt 6pt', border: '0.5pt solid #ccc', background: '#f4f4f4', fontSize: '8pt', textAlign: 'left' }}>UNIT SALE</th>
                <th style={{ padding: '4pt 6pt', border: '0.5pt solid #ccc', background: '#f4f4f4', fontSize: '8pt', textAlign: 'left' }}>UNITS / MASTER CASE</th>
                <th style={{ padding: '4pt 6pt', border: '0.5pt solid #ccc', background: '#f4f4f4', fontSize: '8pt', textAlign: 'left' }}>PACKAGING TYPE</th>
                <th style={{ padding: '4pt 6pt', border: '0.5pt solid #ccc', background: '#f4f4f4', fontSize: '8pt', textAlign: 'left' }}>MASTER CASE DIM.</th>
                <th style={{ padding: '4pt 6pt', border: '0.5pt solid #ccc', background: '#f4f4f4', fontSize: '8pt', textAlign: 'left' }}>CASE UPC</th>
                <th style={{ padding: '4pt 6pt', border: '0.5pt solid #ccc', background: '#f4f4f4', fontSize: '8pt', textAlign: 'left' }}>UNIT UPC</th>
              </tr>
            </thead>
            <tbody>
              {presentationBlocks.map(b => (
                <tr key={b.id}>
                  <td style={{ fontWeight: 'bold' }}>{b.label}</td>
                  <td>{b.unitSale || '—'}</td>
                  <td>{b.unitsPerMasterCase || '—'}</td>
                  <td>{b.packagingType || '—'}</td>
                  <td>{b.masterCaseDimensions || '—'}</td>
                  <td style={{ fontFamily: 'monospace' }}>{b.caseUpc || '—'}</td>
                  <td style={{ fontFamily: 'monospace' }}>{b.unitUpc || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* ════ PAGE 3 — Nutrition Facts ════ */}
        {nutData && (
          <>
            <div className="ts-page-break" />
            <SheetHeader sheetNumber={sheet.sheetNumber} companyName={companyName} logoUrl={companyLogoUrl} page={nextPage()} totalPages={totalPages} version={sheet.version} date={sheet.approvedAt || sheet.createdAt} />
            <div className="ts-section-title">NUTRITION FACTS</div>
            <NutritionFacts data={nutData} />
          </>
        )}

        {/* ════ Retail Packaging — one page per presentation with a photo ════ */}
        {blocksWithPhoto.map(b => (
          <React.Fragment key={`photo-${b.id}`}>
            <div className="ts-page-break" />
            <SheetHeader sheetNumber={sheet.sheetNumber} companyName={companyName} logoUrl={companyLogoUrl} page={nextPage()} totalPages={totalPages} version={sheet.version} date={sheet.approvedAt || sheet.createdAt} />
            <div className="ts-section-title">RETAIL PACKAGING — {b.label?.toUpperCase()}</div>
            <div style={{ textAlign: 'center' }}>
              <img
                src={b.retailPackagingImageUrl}
                alt={`${b.label} retail packaging`}
                style={{ maxWidth: '100%', maxHeight: '400pt', objectFit: 'contain' }}
              />
            </div>
          </React.Fragment>
        ))}

        {/* ════ Pallet Load — one page per presentation with a configured pallet ════ */}
        {blocksWithPallet.map(b => {
          const pallet = b.pallet;
          const palletDerived = b.derived;
          const netGross = computeNetGrossWeight(pallet, b, computedData?.product, computedData?.companyPalletWeightKg, computedData?.productPresentations);
          return (
            <React.Fragment key={`pallet-${b.id}`}>
              <div className="ts-page-break" />
              <SheetHeader sheetNumber={sheet.sheetNumber} companyName={companyName} logoUrl={companyLogoUrl} page={nextPage()} totalPages={totalPages} version={sheet.version} date={sheet.approvedAt || sheet.createdAt} />
              <div className="ts-section-title">PALLET LOAD — {b.label?.toUpperCase()} — {product.name?.toUpperCase()}</div>

              <div className="ts-pallet-block">
                {/* 3D diagram */}
                <div>
                  <div style={{ fontSize: '8pt', fontWeight: 'bold', textAlign: 'center', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11 }}>🗂</span> 3D View
                  </div>
                  <PalletDiagram config={pallet} mode="3d" />
                </div>
                {/* Top-down diagram */}
                <div>
                  <div style={{ fontSize: '8pt', fontWeight: 'bold', textAlign: 'center', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11 }}>🗂</span> Top View (layer arrangement)
                  </div>
                  <PalletDiagram config={pallet} mode="top" />
                </div>
              </div>

              {/* Pallet spec table */}
              <div className="ts-pallet-specs">
                <table>
                  <tbody>
                    <tr>
                      <td>Unit Dimensions:</td>
                      <td>
                        {(pallet.unitDimL || pallet.unitDimW || pallet.unitDimH)
                          ? `${pallet.unitDimL || '?'}L × ${pallet.unitDimW || '?'}W × ${pallet.unitDimH || '?'}H ${pallet.dimUnit || 'in'}`
                          : '—'}
                      </td>
                      <td># units per layer:</td>
                      <td><strong>{pallet.unitsPerLayer || '—'}</strong></td>
                    </tr>
                    <tr>
                      <td>Load Dimensions:</td>
                      <td>
                        {palletDerived.loadL > 0 ? `${palletDerived.loadL}L` : '—'} ×{' '}
                        {palletDerived.loadW > 0 ? `${palletDerived.loadW}W` : '—'} ×{' '}
                        {Number(palletDerived.loadH) > 0 ? `${palletDerived.loadH}H` : '—'} {pallet.dimUnit || 'in'}
                      </td>
                      <td># of layers:</td>
                      <td><strong>{pallet.numberOfLayers || '—'}</strong></td>
                    </tr>
                    <tr>
                      <td>Pallet Dimensions:</td>
                      <td>{pallet.palletL}L × {pallet.palletW}W × {pallet.palletH}H {pallet.dimUnit || 'in'}</td>
                      <td>Total # of packages:</td>
                      <td><strong>{palletDerived.totalPackages || '—'}</strong> {palletDerived.totalPackages > 0 ? 'packages' : ''}</td>
                    </tr>
                    <tr>
                      <td>Total Dimensions:</td>
                      <td>{pallet.palletL}L × {pallet.palletW}W × {palletDerived.totalH}H {pallet.dimUnit || 'in'}</td>
                      <td>Arrangement:</td>
                      <td>
                        <strong>{palletDerived.cols} cols × {palletDerived.rows} rows</strong> per layer
                        {palletDerived.mixedZone && (
                          <> + <strong>{palletDerived.mixedZone.units} rotated</strong> ({palletDerived.mixedZone.cols}×{palletDerived.mixedZone.rows})</>
                        )}
                      </td>
                    </tr>
                    {pallet.totalWeight && (
                      <tr>
                        <td>Total Weight:</td>
                        <td><strong>{pallet.totalWeight} {pallet.weightUnit || 'lbs'}</strong></td>
                        <td>Surface usage:</td>
                        <td>{pallet.surfaceUsage ? `${pallet.surfaceUsage}%` : '—'}</td>
                      </tr>
                    )}
                    {(Number(pallet.overhangW) > 0 || Number(pallet.overhangL) > 0) && (
                      <tr>
                        <td>Overhang (per side):</td>
                        <td colSpan="3">W: {pallet.overhangW || 0} {pallet.dimUnit || 'in'} | L: {pallet.overhangL || 0} {pallet.dimUnit || 'in'}</td>
                      </tr>
                    )}
                    {(netGross.totalNetWeight != null || netGross.totalGrossWeight != null) && (
                      <tr>
                        <td>Total Net Weight:</td>
                        <td>{netGross.totalNetWeight != null ? <strong>{netGross.totalNetWeight.toFixed(1)} {netGross.weightUnit}</strong> : '—'}</td>
                        <td>Total Gross Weight:</td>
                        <td>
                          {netGross.totalGrossWeight != null ? <strong>{netGross.totalGrossWeight.toFixed(1)} {netGross.weightUnit}</strong> : '—'}
                          {netGross.totalGrossWeight != null && (
                            <div style={{ fontSize: '6pt', color: '#999' }}>
                              Includes pallet weight ({netGross.palletWeightDisplay.toFixed(1)} {netGross.weightUnit})
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </React.Fragment>
          );
        })}

        {/* Fixed-position footer — repeats on every printed page (position: fixed
            is re-rendered per page in paged media), so it always sits at the
            bottom regardless of how much content is on a given page. */}
        <Confidential />
      </div>
    </>
  );
}

function formatSheetDate(d) {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function SheetHeader({ sheetNumber, companyName, logoUrl, page, totalPages, version, date }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        gap: '10pt',
        borderBottom: '1.5pt solid #222',
        paddingBottom: '6pt',
        marginBottom: '12pt',
      }}
    >
      {/* Left: sheet number + company name */}
      <div style={{ textAlign: 'left' }}>
        <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.03em' }}>TECH SHEET {sheetNumber || '—'}</div>
        <div style={{ fontSize: '7.5pt', color: '#666', letterSpacing: '0.03em', marginTop: '1pt' }}>{companyName?.toUpperCase()}</div>
      </div>

      {/* Center: logo */}
      <div style={{ textAlign: 'center' }}>
        {logoUrl
          ? <img src={logoUrl} alt={companyName} style={{ maxHeight: 46, maxWidth: 170, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
          : <div style={{ fontSize: '13pt', fontWeight: 700, letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{companyName?.toUpperCase()}</div>
        }
      </div>

      {/* Right: page X of Y, plus revision number + date below it */}
      <div style={{ textAlign: 'right', fontSize: '8pt', fontWeight: 700, whiteSpace: 'nowrap' }}>
        <div>Page {page} of {totalPages}</div>
        {(version || date) && (
          <div style={{ fontSize: '7pt', fontWeight: 400, color: '#666', marginTop: '1pt' }}>
            {version && `Rev. ${version}`}{version && date && ' — '}{formatSheetDate(date)}
          </div>
        )}
      </div>
    </div>
  );
}

function SpecRow({ label, value }) {
  return (
    <tr>
      <td>{label}</td>
      <td>{value}</td>
    </tr>
  );
}

function Confidential() {
  return (
    <div className="ts-confidential">
      The information contained in this document is confidential, privileged and only for the information of the intended recipient and may not be used, published or redistributed without prior written consent.
    </div>
  );
}

function NutritionFacts({ data }) {
  if (!data) return null;

  const servings = data.servingsPerContainer;
  const size = data.servingSizeLabel;

  // Values from the backend are already FDA-rounded (21 CFR 101.9(c)) — fat fields in
  // particular can legitimately be half-gram increments (e.g. 4.5g). Re-rounding here to a
  // whole integer would both misstate the value and break the fat sub-component sum vs. the
  // displayed Total Fat, so we only format for display, never round again.
  const nf = (val, unit = 'g', decimals = null) => {
    if (val == null || val === '') return '—';
    const n = Number(val);
    if (isNaN(n)) return '—';
    if (decimals != null) return `${n.toFixed(decimals)}${unit}`;
    return `${Number.isInteger(n) ? n : n.toFixed(1)}${unit}`;
  };
  // Vitamin/mineral %DV can come back as the literal string '<2' (FDA tiered rounding,
  // 21 CFR 101.9(c)(8)(iii)) rather than a number — pass it through as-is instead of letting
  // Number('<2') => NaN swallow it into a blank.
  const pct = (val) => {
    if (val === '<2') return '<2%';
    return val != null && !isNaN(Number(val)) ? `${Math.round(Number(val))}%` : '';
  };

  // Per-100g-of-product figures, computed straight from the unrounded raw values (never
  // from the FDA-rounded ones above — see docs/NUTRITION_LABEL_MODULE.md's raw-values-first
  // rule). Reference/QA figures only, not a regulatory declaration, so shown unrounded rather
  // than run through 21 CFR 101.9 rounding meant for per-serving amounts.
  const raw = data.rawValues;
  const servingSizeG = data.servingSize;
  const per100 = (field) => {
    const v = raw?.[field];
    if (v == null || !servingSizeG) return null;
    const n = (Number(v) / Number(servingSizeG)) * 100;
    return isNaN(n) ? null : n;
  };
  const fmt100 = (n, unit = 'g', decimals = 2) => n == null ? '—' : `${n.toFixed(decimals)}${unit}`;
  const showPer100 = !!(raw && servingSizeG);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: '14pt', alignItems: 'flex-start' }}>
      <div className="ts-nf-box">
        <div className="ts-nf-title">Nutrition Facts</div>
        {servings && <div style={{ fontSize: '9pt', borderBottom: '0.5pt solid #1a1a1a', padding: '2pt 0' }}>{servings} servings per container</div>}
        <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '4pt solid #1a1a1a', padding: '2pt 0', fontWeight: 'bold', fontSize: '9pt' }}>
          <span>Serving size</span>
          <span>{size || '—'}</span>
        </div>

        <div style={{ borderBottom: '1pt solid #1a1a1a', padding: '2pt 0', fontSize: '7pt' }}>Amounts per serving</div>
        <div className="ts-nf-row thick">
          <span style={{ fontSize: '14pt', fontWeight: '900' }}>Calories</span>
          <span style={{ fontSize: '20pt', fontWeight: '900' }}>{data.calories ?? '—'}</span>
        </div>
        <div style={{ textAlign: 'right', fontSize: '7.5pt', borderBottom: '0.5pt solid #1a1a1a', padding: '2pt 0' }}>
          Amount % Daily Value*
        </div>

        <NfRow label="Total Fat" value={nf(data.totalFat)} dv={pct(data.totalFatDV)} bold />
        <NfRow label="Saturated Fat" value={nf(data.saturatedFat)} dv={pct(data.saturatedFatDV)} indent />
        <NfRow label="Trans Fat" value={nf(data.transFat)} indent />
        {/* Poly/monounsaturated fat are voluntary FDA declarations, removed entirely from the
            tech sheet (not just suppressed when zero) — the underlying per-ingredient values
            don't reconcile precisely against Total Fat/Saturated Fat (sub-fats not summing to
            the declared total), and printing an imprecise number next to a correct one is worse
            than omitting a voluntary field. */}
        <NfRow label="Cholesterol" value={nf(data.cholesterol, 'mg')} dv={pct(data.cholesterolDV)} bold />
        <NfRow label="Sodium" value={nf(data.sodium, 'mg')} dv={pct(data.sodiumDV)} bold />
        <NfRow label="Total Carbohydrate" value={nf(data.totalCarbs)} dv={pct(data.totalCarbsDV)} bold />
        <NfRow label="Dietary Fiber" value={nf(data.dietaryFiber)} dv={pct(data.dietaryFiberDV)} indent />
        <NfRow label="Total Sugars (Includes added sugars)" value={nf(data.totalSugars)} indent />
        <NfRow label="Added Sugars" value={nf(data.addedSugars)} dv={pct(data.addedSugarsDV)} indent style={{ borderBottom: '4pt solid #1a1a1a' }} />
        <NfRow label="Protein" value={nf(data.protein)} bold />

        <div style={{ borderTop: '0.5pt solid #ccc' }}>
          {data.vitaminD != null && <NfRow label="Vitamin D" value={nf(data.vitaminD, 'mcg', 1)} dv={pct(data.vitaminDDV)} />}
          {data.calcium != null && <NfRow label="Calcium" value={nf(data.calcium, 'mg')} dv={pct(data.calciumDV)} />}
          {data.iron != null && <NfRow label="Iron" value={nf(data.iron, 'mg', 1)} dv={pct(data.ironDV)} />}
          {data.potassium != null && <NfRow label="Potassium" value={nf(data.potassium, 'mg')} dv={pct(data.potassiumDV)} />}
        </div>

        <div className="ts-nf-dv">
          * The % Daily Value (DV) tells you how much a nutrient in a serving food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.
        </div>
      </div>

      {showPer100 && (
        <div className="ts-nf100-box">
          <div className="ts-nf100-title">
            Per 100g of Product
            <div className="ts-nf100-subtitle">(unrounded — reference / QA only)</div>
          </div>
          <div className="ts-nf100-row"><span>Calories</span><span>{fmt100(per100('calories'), '', 1)}</span></div>
          <div className="ts-nf100-row"><span>Total Fat</span><span>{fmt100(per100('totalFat'))}</span></div>
          <div className="ts-nf100-row indent"><span>Saturated Fat</span><span>{fmt100(per100('saturatedFat'))}</span></div>
          <div className="ts-nf100-row indent"><span>Trans Fat</span><span>{fmt100(per100('transFat'))}</span></div>
          <div className="ts-nf100-row"><span>Cholesterol</span><span>{fmt100(per100('cholesterol'), 'mg')}</span></div>
          <div className="ts-nf100-row"><span>Sodium</span><span>{fmt100(per100('sodium'), 'mg')}</span></div>
          <div className="ts-nf100-row"><span>Total Carbohydrate</span><span>{fmt100(per100('totalCarbs'))}</span></div>
          <div className="ts-nf100-row indent"><span>Dietary Fiber</span><span>{fmt100(per100('dietaryFiber'))}</span></div>
          <div className="ts-nf100-row indent"><span>Total Sugars</span><span>{fmt100(per100('totalSugars'))}</span></div>
          <div className="ts-nf100-row indent"><span>Added Sugars</span><span>{fmt100(per100('addedSugars'))}</span></div>
          <div className="ts-nf100-row"><span>Protein</span><span>{fmt100(per100('protein'))}</span></div>
          {data.vitaminD != null && <div className="ts-nf100-row"><span>Vitamin D</span><span>{fmt100(per100('vitaminD'), 'mcg')}</span></div>}
          {data.calcium != null && <div className="ts-nf100-row"><span>Calcium</span><span>{fmt100(per100('calcium'), 'mg')}</span></div>}
          {data.iron != null && <div className="ts-nf100-row"><span>Iron</span><span>{fmt100(per100('iron'), 'mg')}</span></div>}
          {data.potassium != null && <div className="ts-nf100-row"><span>Potassium</span><span>{fmt100(per100('potassium'), 'mg')}</span></div>}
        </div>
      )}
    </div>
  );
}

function NfRow({ label, value, dv, bold, indent, style: extraStyle }) {
  return (
    <div
      className={`ts-nf-row${bold ? ' bold' : ''}${indent ? ' indent' : ''}`}
      style={extraStyle}
    >
      <span>{label} <strong>{value}</strong></span>
      {dv && <span>{dv}</span>}
    </div>
  );
}
